import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaLocationArrow, FaTag, FaList, FaPaw, FaHashtag, FaBirthdayCake, FaWeight, FaRupeeSign, FaAlignLeft, FaMapMarkerAlt, FaCity, FaMapPin, FaImages, FaPlus, FaMinus, FaArrowLeft, FaArrowRight
} from 'react-icons/fa';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import CategoryMenu from '../components/CategoryMenu';
import { getStates, getCitiesByState, getCurrentLocation, getLocationFromCoordinates } from '../services/locationService';
import { toast } from 'react-toastify';
import './PostAd.css';
import { API_ENDPOINTS } from '../config/api';
import BackToHome from '../components/BackToHome';
import { sanitizeInput, validateTitle, validateDescription, validatePrice, sanitizeFormData } from '../utils/security';

const PostAd = () => {
  const { categoryId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(0); // 0 = Screen 1, 1 = Screen 2
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    priceType: 'Per Unit',
    state: '',
    city: '',
    neighbourhood: '',
    category: categoryId || '',
    type: '',
    breed: '',
    quantity: 1,
    age: '',
    ageUnit: 'Years',
    weight: '',
    weightUnit: 'Kgs',
    milkCapacity: '',
    gender: '',
    status: '',
    runningMonth: '',
    size: '',
    images: []
  });
  
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationTab, setLocationTab] = useState('list');
  const [detectedLocation, setDetectedLocation] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [userCoords, setUserCoords] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStates(getStates());
    if (categoryId) {
      setFormData(prev => ({ ...prev, category: categoryId }));
    }
  }, [categoryId]);

  useEffect(() => {
    if (formData.state) {
      const citiesList = getCitiesByState(formData.state);
      setCities(citiesList);
      setFormData(prev => ({ ...prev, city: '', neighbourhood: '' }));
    } else {
      setCities([]);
    }
  }, [formData.state]);

  useEffect(() => {
    if (formData.city && cities.length > 0) {
      const selectedCity = cities.find(city => city.name === formData.city);
      setNeighborhoods(selectedCity && Array.isArray(selectedCity.neighborhoods) ? selectedCity.neighborhoods : []);
    } else {
      setNeighborhoods([]);
    }
  }, [formData.city, cities]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize input based on field type
    let sanitizedValue = value;
    if (typeof value === 'string') {
      // Sanitize text inputs to prevent XSS
      if (name === 'title' || name === 'description' || name === 'breed' || name === 'type' || name === 'size') {
        sanitizedValue = sanitizeInput(value);
      } else if (name === 'price' || name === 'age' || name === 'weight' || name === 'quantity') {
        // For numeric fields, only allow numbers and decimal points
        sanitizedValue = value.replace(/[^0-9.]/g, '');
      } else {
        // For other text fields, sanitize
        sanitizedValue = sanitizeInput(value);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue,
      ...(name === 'state' && { city: '', neighbourhood: '' }),
      ...(name === 'city' && { neighbourhood: '' }),
      ...(name === 'status' && { milkCapacity: '', runningMonth: '' })
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleQuantityChange = (delta) => {
    const maxQuantity = getMaxQuantity();
    setFormData(prev => {
      const newQuantity = Math.max(1, Math.min(maxQuantity, prev.quantity + delta));
      return { ...prev, quantity: newQuantity };
    });
  };

  const getMaxQuantity = () => {
    switch (categoryId) {
      case 'chickens': return 100;
      case 'pets': return formData.type === 'Aquarium Fishes' ? 50 : 20;
      case 'cattle': return 10;
      case 'sheep-goat': return 50;
      default: return 50;
    }
  };

  const handleImageChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFormData(prev => {
      const updatedImages = [...prev.images, ...newFiles].slice(0, 5);
      return { ...prev, images: updatedImages };
    });
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleUseCurrentLocation = async () => {
    try {
      setLocationLoading(true);
      const position = await getCurrentLocation();
      const location = await getLocationFromCoordinates(position.latitude, position.longitude);
      setUserCoords({ latitude: position.latitude, longitude: position.longitude });
      setDetectedLocation(location);
      setFormData(prev => ({
        ...prev,
        state: location.state || prev.state,
        city: location.city || prev.city,
        neighbourhood: location.neighborhood || prev.neighbourhood
      }));
      toast.success('Location detected successfully!');
    } catch (error) {
      console.error('Error getting location:', error);
      toast.error('Failed to get your location. Please select manually.');
    } finally {
      setLocationLoading(false);
    }
  };

  const isScreen1Valid = useMemo(() => {
    const title = formData.title ? String(formData.title).trim() : '';
    const price = formData.price ? String(formData.price).trim() : '';
    const description = formData.description ? String(formData.description).trim() : '';
    
    const titleValid = title.length >= 10 && title.length <= 100;
    const priceValid = price !== '' && !isNaN(parseFloat(price)) && parseFloat(price) > 0;
    const descriptionValid = description.length >= 20 && description.length <= 500;
    const imagesValid = formData.images && Array.isArray(formData.images) && formData.images.length > 0;
    
    const isValid = titleValid && priceValid && descriptionValid && imagesValid;
    
    // Debug log (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('Validation check:', {
        title: { value: title, length: title.length, valid: titleValid },
        price: { value: price, valid: priceValid },
        description: { value: description.substring(0, 50) + '...', length: description.length, valid: descriptionValid },
        images: { count: formData.images?.length || 0, valid: imagesValid },
        overall: isValid
      });
    }
    
    return isValid;
  }, [formData.title, formData.price, formData.description, formData.images]);

  const validateScreen1 = () => {
    const newErrors = {};
    if (!formData.title || formData.title.length < 10) {
      newErrors.title = 'Title is required and must be at least 10 characters';
    }
    if (formData.title && formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }
    if (!formData.price) {
      newErrors.price = 'Price is required';
    }
    if (!formData.description || formData.description.length < 20) {
      newErrors.description = 'Description is required and must be at least 20 characters';
    }
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }
    if (formData.images.length === 0) {
      newErrors.images = 'At least one image is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateScreen2 = () => {
    const newErrors = {};
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.city) {
      newErrors.city = 'City is required';
    }
    
    // Category-specific validations
    if (categoryId === 'cattle' && (formData.type === 'Cows' || formData.type === 'Buffalo')) {
      if (!formData.status) {
        newErrors.status = 'Status is required for Cows and Buffalo';
      } else {
        // If status is milking, milkCapacity is required
        if (formData.status === 'milk' && !formData.milkCapacity) {
          newErrors.milkCapacity = 'Milk capacity is required when status is Milking';
        }
        // If status is pregnant, runningMonth is required
        if (formData.status === 'pregnancy' && !formData.runningMonth) {
          newErrors.runningMonth = 'Running month is required when status is Pregnant';
        }
      }
    }
    
    if ((categoryId === 'pets' && formData.type !== 'Aquarium Fishes') || categoryId === 'sheep-goat') {
      if (!formData.gender) {
        newErrors.gender = 'Gender is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goToScreen2 = () => {
    if (validateScreen1()) {
      setCurrentStep(1);
    }
  };

  const goToScreen1 = () => {
    setCurrentStep(0);
  };

  const handleSubmit = async () => {
    if (!validateScreen2()) {
      return;
    }

    if (!currentUser) {
      toast.error('Please log in to post an ad');
      navigate('/login');
      return;
    }

    try {
      setSubmitting(true);
      
      // Sanitize all form data before sending
      const sanitizedFormData = sanitizeFormData(formData);
      
      // Additional validation with security utilities
      const titleValidation = validateTitle(sanitizedFormData.title);
      if (!titleValidation.valid) {
        setErrors({ title: titleValidation.error });
        toast.error(titleValidation.error);
        setSubmitting(false);
        return;
      }
      
      const descriptionValidation = validateDescription(sanitizedFormData.description);
      if (!descriptionValidation.valid) {
        setErrors({ description: descriptionValidation.error });
        toast.error(descriptionValidation.error);
        setSubmitting(false);
        return;
      }
      
      const priceValidation = validatePrice(sanitizedFormData.price);
      if (!priceValidation.valid) {
        setErrors({ price: priceValidation.error });
        toast.error(priceValidation.error);
        setSubmitting(false);
        return;
      }
      
      const formDataToSend = new FormData();

      // Basic fields (use sanitized values)
      formDataToSend.append('title', titleValidation.sanitized);
      formDataToSend.append('description', descriptionValidation.sanitized);
      formDataToSend.append('price', priceValidation.sanitized);
      formDataToSend.append('priceType', formData.priceType);
      formDataToSend.append('category', categoryId);
      formDataToSend.append('state', sanitizedFormData.state);
      formDataToSend.append('city', sanitizedFormData.city);
      formDataToSend.append('neighbourhood', sanitizedFormData.neighbourhood || '');

      // Type (sanitized)
      if (sanitizedFormData.type) formDataToSend.append('type', sanitizedFormData.type);

      // Breed (sanitized)
      if (sanitizedFormData.breed) formDataToSend.append('breed', sanitizedFormData.breed);

      // Quantity
      formDataToSend.append('quantity', formData.quantity);

      // Age with unit (sanitized)
      if (sanitizedFormData.age) {
        const sanitizedAge = sanitizeInput(sanitizedFormData.age.toString());
        formDataToSend.append('age', `${sanitizedAge} ${sanitizedFormData.ageUnit}`);
      }

      // Weight with unit conversion (sanitized)
      if (sanitizedFormData.weight) {
        const weightNum = parseFloat(sanitizedFormData.weight);
        if (!isNaN(weightNum)) {
          const weightInKg = sanitizedFormData.weightUnit === 'Grams' ? weightNum / 1000 : weightNum;
          formDataToSend.append('weight', weightInKg.toString());
        }
      }

      // Gender
      if (formData.gender) {
        const genderMap = { 'male': 'Male', 'female': 'Female', 'pair': 'Pair' };
        formDataToSend.append('gender', genderMap[formData.gender] || formData.gender);
      }

      // Status for cattle
      if (formData.status && (formData.type === 'Cows' || formData.type === 'Buffalo')) {
        const statusMap = { 'milk': 'milking', 'pregnancy': 'pregnant', 'dry': 'dry' };
        formDataToSend.append('cattleStatus', statusMap[formData.status] || formData.status);
        
        // Milk capacity - backend requires it for all Cow/Buffalo, but only meaningful when milking
        if (formData.status === 'milk' && formData.milkCapacity) {
          formDataToSend.append('milkCapacity', `${formData.milkCapacity} liters per day`);
        } else {
          // Send placeholder value for non-milking statuses to satisfy backend validation
          // Backend validation incorrectly requires milkCapacity for all statuses, not just "milking"
          // Using "0 liters per day" as a valid placeholder that matches the expected format
          formDataToSend.append('milkCapacity', '0 liters per day');
        }
        
        // Running month - only send when status is 'pregnancy'
        if (formData.status === 'pregnancy' && formData.runningMonth) {
          formDataToSend.append('runningMonth', `${formData.runningMonth} ${formData.runningMonth === '1' ? 'Month' : 'Months'}`);
        }
      }

      // Size (sanitized)
      if (sanitizedFormData.size) formDataToSend.append('size', sanitizedFormData.size);

      // Images
      formData.images.forEach(image => {
        formDataToSend.append('images', image);
      });

      const token = await currentUser.getIdToken();
      const response = await axios.post(API_ENDPOINTS.ADS.CREATE, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 201) {
        toast.success('Ad posted successfully!');
        navigate('/');
      }
    } catch (error) {
      console.error('Error posting ad:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Error posting ad';
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!categoryId) {
    return <CategoryMenu />;
  }

  const getCategoryName = () => {
    switch (categoryId) {
      case 'chickens': return 'Chicken';
      case 'pets': return 'Pet';
      case 'cattle': return 'Cattle';
      case 'sheep-goat': return 'Sheep/Goat';
      default: return 'Animal';
    }
  };

  const isCattleCowOrBuffalo = categoryId === 'cattle' && (formData.type === 'Cows' || formData.type === 'Buffalo');
  const isPetsNeedsGender = categoryId === 'pets' && formData.type !== 'Aquarium Fishes';
  const isSheepGoat = categoryId === 'sheep-goat';

  return (
    <div className="post-ad-wrapper">
      <Container>
        <BackToHome />
      </Container>
      <div className="post-ad-container">
        {/* Header */}
        <div className="post-ad-header">
          <h2 className="post-ad-title">
            {currentStep === 0 
              ? `${getCategoryName()} - Basic Information`
              : `${getCategoryName()} - Additional Details`}
          </h2>
          <div className="progress-indicator">
            <div className={`progress-bar ${currentStep >= 0 ? 'active' : ''}`}></div>
            <div className={`progress-bar ${currentStep >= 1 ? 'active' : ''}`}></div>
          </div>
        </div>

        {currentStep === 0 ? (
          /* Screen 1: Basic Information */
          <Form onSubmit={(e) => { 
            e.preventDefault(); 
            if (isScreen1Valid) {
              goToScreen2();
            } else {
              validateScreen1(); // Show errors
            }
          }}>
            {/* Title */}
            <div className="form-field-group">
              <label className="form-label">
                <FaTag className="label-icon" />
                Title <span className="required">*</span>
              </label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter ad title (min 10 characters)"
                maxLength={100}
                className={errors.title ? 'is-invalid' : ''}
              />
              {errors.title && <div className="error-message">{errors.title}</div>}
              <div className="field-hint">{formData.title.length}/100 characters</div>
            </div>

            {/* Price with Type */}
            <div className="form-field-group">
              <label className="form-label">
                <FaRupeeSign className="label-icon" />
                Price (₹) <span className="required">*</span>
              </label>
              <div className="price-field-wrapper">
                <Form.Control
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Enter price"
                  className={errors.price ? 'is-invalid' : ''}
                />
                <div className="price-divider"></div>
                <Form.Select
                  name="priceType"
                  value={formData.priceType}
                  onChange={handleChange}
                  className="price-type-select"
                >
                  <option value="Per Unit">Per Unit</option>
                  <option value="For All">For All</option>
                </Form.Select>
              </div>
              {errors.price && <div className="error-message">{errors.price}</div>}
            </div>

            {/* Description */}
            <div className="form-field-group">
              <label className="form-label">
                <FaAlignLeft className="label-icon" />
                Description <span className="required">*</span>
              </label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description (min 20 characters)"
                maxLength={500}
                className={errors.description ? 'is-invalid' : ''}
              />
              {errors.description && <div className="error-message">{errors.description}</div>}
              <div className="field-hint">{formData.description.length}/500 characters</div>
            </div>

            {/* Images */}
            <div className="form-field-group">
              <label className="form-label">
                <FaImages className="label-icon" />
                Add Photos <span className="required">*</span>
              </label>
              <div className="image-grid-container">
                <div className="image-grid">
                  {formData.images.map((image, index) => (
                    <div key={index} className="image-item">
                      <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                      {index === 0 && <span className="cover-badge">Cover</span>}
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={() => handleRemoveImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {formData.images.length < 5 && (
                    <label className="add-image-btn">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        style={{ display: 'none' }}
                      />
                      <FaPlus />
                      <span>Add Photo</span>
                    </label>
                  )}
                </div>
              </div>
              {errors.images && <div className="error-message">{errors.images}</div>}
              <div className="field-hint">You can add up to 5 images. First image will be the cover photo.</div>
            </div>

            {/* Next Button */}
            <div className="form-actions">
              <Button
                type="submit"
                className={`btn-primary-large ${isScreen1Valid ? 'btn-enabled' : 'btn-disabled'}`}
                disabled={!isScreen1Valid}
                onClick={(e) => {
                  if (!isScreen1Valid) {
                    e.preventDefault();
                    validateScreen1(); // Show validation errors
                  }
                }}
              >
                Next: Add Details <FaArrowRight className="ms-2" />
              </Button>
              {!isScreen1Valid && (
                <div className="validation-hint mt-2">
                  <strong>Please complete all required fields:</strong>
                  <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.875rem' }}>
                    {(!formData.title || formData.title.trim().length < 10 || formData.title.trim().length > 100) && (
                      <li>Title must be 10-100 characters (current: {formData.title?.trim().length || 0})</li>
                    )}
                    {(!formData.price || !parseFloat(formData.price) || parseFloat(formData.price) <= 0) && (
                      <li>Price must be a valid number greater than 0</li>
                    )}
                    {(!formData.description || formData.description.trim().length < 20 || formData.description.trim().length > 500) && (
                      <li>Description must be 20-500 characters (current: {formData.description?.trim().length || 0})</li>
                    )}
                    {(!formData.images || formData.images.length === 0) && (
                      <li>At least one image is required</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </Form>
        ) : (
          /* Screen 2: Additional Details & Location */
          <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* Additional Details */}
            <div className="form-section">
              <h5 className="section-title">Additional Details (Optional)</h5>
              <div className="section-description">
                Fill in these fields to help buyers find your ad more easily
              </div>

              <div className="details-container">
                {/* Type */}
                <div className="form-field-group">
                  <label className="form-label">
                    <FaList className="label-icon" />
                    Type
                  </label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                  >
                    <option value="">Select Type</option>
                    {categoryId === 'chickens' && (
                      <>
                        <option value="Hen">Hen</option>
                        <option value="Rooster">Rooster</option>
                        <option value="Chicks">Chicks</option>
                        <option value="Breeding Pair">Breeding Pair</option>
                      </>
                    )}
                    {categoryId === 'pets' && (
                      <>
                        <option value="Dogs">Dogs</option>
                        <option value="Puppies">Puppies</option>
                        <option value="Cats">Cats</option>
                        <option value="Rabbits">Rabbits</option>
                        <option value="Birds">Birds</option>
                        <option value="Aquarium Fishes">Aquarium Fishes</option>
                      </>
                    )}
                    {categoryId === 'cattle' && (
                      <>
                        <option value="Cows">Cows</option>
                        <option value="Ox">Ox</option>
                        <option value="Buffalo">Buffalo</option>
                        <option value="Calves">Calves</option>
                      </>
                    )}
                    {categoryId === 'sheep-goat' && (
                      <>
                        <option value="Sheep">Sheep</option>
                        <option value="Goat">Goat</option>
                      </>
                    )}
                  </Form.Select>
                </div>

                {/* Breed */}
                <div className="form-field-group">
                  <label className="form-label">
                    <FaPaw className="label-icon" />
                    {categoryId === 'pets' && formData.type === 'Aquarium Fishes' ? 'Fish Name' :
                     categoryId === 'pets' && formData.type === 'Birds' ? 'Bird Name' : 'Breed'}
                  </label>
                  <Form.Control
                    type="text"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    placeholder={categoryId === 'pets' && formData.type === 'Aquarium Fishes' ? 'Enter fish name' :
                                 categoryId === 'pets' && formData.type === 'Birds' ? 'Enter bird name' : 'Enter breed'}
                    maxLength={50}
                  />
                </div>

                {/* Age and Weight/Size - Category specific */}
                {categoryId === 'pets' && formData.type === 'Aquarium Fishes' ? (
                  <Row>
                    <Col md={6}>
                      <div className="form-field-group">
                        <label className="form-label">
                          <FaBirthdayCake className="label-icon" />
                          Age
                        </label>
                        <div className="field-with-unit">
                          <Form.Control
                            type="number"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            placeholder="e.g., 6"
                          />
                          <Form.Select
                            name="ageUnit"
                            value={formData.ageUnit}
                            onChange={handleChange}
                            className="unit-select"
                          >
                            <option value="Years">Years</option>
                            <option value="Months">Months</option>
                          </Form.Select>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="form-field-group">
                        <label className="form-label">Size</label>
                        <Form.Control
                          type="text"
                          name="size"
                          value={formData.size}
                          onChange={handleChange}
                          placeholder="Size"
                          maxLength={20}
                        />
                      </div>
                    </Col>
                  </Row>
                ) : categoryId === 'pets' ? (
                  <div className="form-field-group">
                    <label className="form-label">
                      <FaBirthdayCake className="label-icon" />
                      Age
                    </label>
                    <div className="field-with-unit">
                      <Form.Control
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        placeholder="e.g., 6"
                      />
                      <Form.Select
                        name="ageUnit"
                        value={formData.ageUnit}
                        onChange={handleChange}
                        className="unit-select"
                      >
                        <option value="Years">Years</option>
                        <option value="Months">Months</option>
                      </Form.Select>
                    </div>
                  </div>
                ) : categoryId === 'cattle' ? (
                  <div className="form-field-group">
                    <label className="form-label">
                      <FaBirthdayCake className="label-icon" />
                      Age
                    </label>
                    <div className="field-with-unit">
                      <Form.Control
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        placeholder="e.g., 6"
                      />
                      <Form.Select
                        name="ageUnit"
                        value={formData.ageUnit}
                        onChange={handleChange}
                        className="unit-select"
                      >
                        <option value="Years">Years</option>
                        <option value="Months">Months</option>
                      </Form.Select>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="form-field-group">
                      <label className="form-label">
                        <FaBirthdayCake className="label-icon" />
                        Age
                      </label>
                      <div className="field-with-unit">
                        <Form.Control
                          type="number"
                          name="age"
                          value={formData.age}
                          onChange={handleChange}
                          placeholder="e.g., 6"
                        />
                        <Form.Select
                          name="ageUnit"
                          value={formData.ageUnit}
                          onChange={handleChange}
                          className="unit-select"
                        >
                          <option value="Years">Years</option>
                          <option value="Months">Months</option>
                        </Form.Select>
                      </div>
                    </div>
                    <div className="form-field-group">
                      <label className="form-label">
                        <FaWeight className="label-icon" />
                        Weight
                      </label>
                      <div className="field-with-unit">
                        <Form.Control
                          type="number"
                          step="0.1"
                          name="weight"
                          value={formData.weight}
                          onChange={handleChange}
                          placeholder="e.g., 2.5"
                        />
                        <Form.Select
                          name="weightUnit"
                          value={formData.weightUnit}
                          onChange={handleChange}
                          className="unit-select"
                        >
                          <option value="Kgs">Kgs</option>
                          <option value="Grams">Grams</option>
                        </Form.Select>
                      </div>
                    </div>
                  </>
                )}

                {/* Quantity Stepper */}
                {!(categoryId === 'cattle' && isCattleCowOrBuffalo) && (
                  <div className="form-field-group">
                    <label className="form-label">
                      <FaHashtag className="label-icon" />
                      Quantity
                    </label>
                    <div className="quantity-stepper">
                      <button
                        type="button"
                        className="stepper-btn"
                        onClick={() => handleQuantityChange(-1)}
                        disabled={formData.quantity <= 1}
                      >
                        <FaMinus />
                      </button>
                      <div className="quantity-display">{formData.quantity}</div>
                      <button
                        type="button"
                        className="stepper-btn"
                        onClick={() => handleQuantityChange(1)}
                        disabled={formData.quantity >= getMaxQuantity()}
                      >
                        <FaPlus />
                      </button>
                    </div>
                    <div className="field-hint">Min: 1, Max: {getMaxQuantity()}</div>
                  </div>
                )}

                {/* Gender - for pets (except Aquarium Fishes) and sheep-goat */}
                {(isPetsNeedsGender || isSheepGoat) && (
                  <div className="form-field-group">
                    <label className="form-label">
                      Gender {isPetsNeedsGender || isSheepGoat ? <span className="required">*</span> : ''}
                    </label>
                    <Form.Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={errors.gender ? 'is-invalid' : ''}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="pair">Pair</option>
                    </Form.Select>
                    {errors.gender && <div className="error-message">{errors.gender}</div>}
                  </div>
                )}

                {/* Status - for cattle (Cows/Buffalo only) */}
                {isCattleCowOrBuffalo && (
                  <>
                    <div className="form-field-group">
                      <label className="form-label">
                        Status <span className="required">*</span>
                      </label>
                      <Form.Select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className={errors.status ? 'is-invalid' : ''}
                      >
                        <option value="">Select Status</option>
                        <option value="milk">Milking</option>
                        <option value="pregnancy">Pregnant</option>
                        <option value="dry">Dry</option>
                      </Form.Select>
                      {errors.status && <div className="error-message">{errors.status}</div>}
                    </div>

                    {/* Milk Capacity - if status is milking */}
                    {formData.status === 'milk' && (
                      <div className="form-field-group">
                        <label className="form-label">Milk Capacity</label>
                        <Form.Select
                          name="milkCapacity"
                          value={formData.milkCapacity}
                          onChange={handleChange}
                        >
                          <option value="">Select Capacity</option>
                          <option value="Below 5">Below 5 liters per day</option>
                          <option value="5-10">5-10 liters per day</option>
                          <option value="10-15">10-15 liters per day</option>
                          <option value="15-20">15-20 liters per day</option>
                          <option value="20+">20+ liters per day</option>
                        </Form.Select>
                      </div>
                    )}

                    {/* Running Month - if status is pregnant */}
                    {formData.status === 'pregnancy' && (
                      <div className="form-field-group">
                        <label className="form-label">Running Month</label>
                        <Form.Select
                          name="runningMonth"
                          value={formData.runningMonth}
                          onChange={handleChange}
                        >
                          <option value="">Select Month</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={num.toString()}>
                              {num} {num === 1 ? 'Month' : 'Months'}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Location */}
            <div className="form-section">
              <h5 className="section-title">
                <FaMapMarkerAlt className="me-2" />
                Location <span className="required">*</span>
              </h5>

              <div className="location-container">
                <div className="location-tabs">
                  <button
                    type="button"
                    className={`location-tab ${locationTab === 'list' ? 'active' : ''}`}
                    onClick={() => setLocationTab('list')}
                  >
                    <FaList className="me-2" />
                    Select Location
                  </button>
                  <button
                    type="button"
                    className={`location-tab ${locationTab === 'current' ? 'active' : ''}`}
                    onClick={() => setLocationTab('current')}
                  >
                    <FaLocationArrow className="me-2" />
                    Current Location
                  </button>
                </div>

                <div className="location-content">
                  {locationTab === 'list' ? (
                    <>
                      <div className="form-field-group">
                        <label className="form-label">
                          <FaMapMarkerAlt className="label-icon" />
                          State <span className="required">*</span>
                        </label>
                        <Form.Select
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          className={errors.state ? 'is-invalid' : ''}
                        >
                          <option value="">Select State</option>
                          {states.map(state => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </Form.Select>
                        {errors.state && <div className="error-message">{errors.state}</div>}
                      </div>

                      {formData.state && (
                        <div className="form-field-group">
                          <label className="form-label">
                            <FaCity className="label-icon" />
                            City <span className="required">*</span>
                          </label>
                          <Form.Select
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            disabled={!formData.state}
                            className={errors.city ? 'is-invalid' : ''}
                          >
                            <option value="">Select City</option>
                            {cities.map(city => (
                              <option key={city.name} value={city.name}>{city.name}</option>
                            ))}
                          </Form.Select>
                          {errors.city && <div className="error-message">{errors.city}</div>}
                        </div>
                      )}

                      {formData.city && neighborhoods.length > 0 && (
                        <div className="form-field-group">
                          <label className="form-label">
                            <FaMapPin className="label-icon" />
                            Neighbourhood (Optional)
                          </label>
                          <Form.Select
                            name="neighbourhood"
                            value={formData.neighbourhood}
                            onChange={handleChange}
                            disabled={!formData.city || neighborhoods.length === 0}
                          >
                            <option value="">Select Neighbourhood</option>
                            {neighborhoods.map(neighbourhood => (
                              <option key={neighbourhood} value={neighbourhood}>{neighbourhood}</option>
                            ))}
                          </Form.Select>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="current-location-content">
                      <Button
                        type="button"
                        variant="outline-primary"
                        onClick={handleUseCurrentLocation}
                        disabled={locationLoading}
                        className="current-location-btn"
                      >
                        <FaLocationArrow className="me-2" />
                        {locationLoading ? 'Detecting Location...' : 'Get Current Location'}
                      </Button>
                      {detectedLocation && (
                        <Alert variant="success" className="mt-3">
                          <strong>Detected Location:</strong><br />
                          State: {detectedLocation.state || '-'}<br />
                          City: {detectedLocation.city || '-'}<br />
                          Neighbourhood: {detectedLocation.neighborhood || '-'}
                        </Alert>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <Row>
                <Col md={6}>
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={goToScreen1}
                    className="btn-outline-large"
                  >
                    <FaArrowLeft className="me-2" />
                    Back
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    type="submit"
                    className="btn-primary-large"
                    disabled={submitting}
                  >
                    {submitting ? 'Posting...' : 'Post Ad'}
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default PostAd;
